<script setup lang="ts">
  import SearchParams from '@/components/SearchParams.vue'
  import RecyclistNative from '@/components/RecyclistNative.vue'
  import CardComponent from '@/components/CardComponent.vue'
  import { ref, computed, toRefs, useTemplateRef } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { useNetwork, useThrottleFn } from '@vueuse/core'
  import { ChevronDoubleUpIcon } from '@heroicons/vue/24/outline'
  import { useDeckStore } from '@/store'
  import { cleanColumns } from '@/store/mutations'
  import { useNotify } from '@/composables/useNotify'
  import { extractSearchTerms } from '@/lib/searchTerms'

  const { t } = useI18n({ useScope: 'global' })
  const notify = useNotify()

  const props = defineProps({
    columnId: {
      type: String,
      required: true
    }
  })

  const { columnId } = toRefs(props)

  const deckStore = useDeckStore()
  const { isOnline } = useNetwork()
  const {
    getColumnById,
    getDocumentsByColumnId,
    resetColumn,
    closeColumn,
    refreshColumn,
    toggleAgenda
  } = deckStore

  const searchTerms = computed(() => {
    const col = getColumnById(columnId.value)
    if (!col) return []
    return extractSearchTerms(col.params.query)
  })

  const noMore = ref(false)
  const isLoading = ref(false)
  const error = ref(false)

  const documents = computed(() => getDocumentsByColumnId(columnId.value))

  function reset() {
    noMore.value = false
    resetColumn(columnId.value)
    cleanColumns()
  }

  async function loadOlder() {
    if (!isOnline.value) return false
    // Pas de refresh 'older' pendant qu'un 'newer' est en vol ; la sentinelle
    // de RecyclistNative réapparaîtra en fin de chargement et relancera l'appel
    if (isLoading.value) return false
    isLoading.value = true
    error.value = false
    try {
      noMore.value = (await refreshColumn(columnId.value, 'older')).length === 0
    } catch (e) {
      notify.error(e instanceof Error ? e.message : 'Erreur lors du chargement')
      error.value = true
    } finally {
      isLoading.value = false
    }
  }

  const loadOlderThrottle = useThrottleFn(loadOlder, 50)

  async function loadNewer() {
    if (!isOnline.value) return false
    if (recyclist.value && recyclist.value.y >= 900) return false
    isLoading.value = true
    error.value = false
    try {
      await refreshColumn(columnId.value, 'newer')
    } catch (e) {
      notify.error(e instanceof Error ? e.message : 'Erreur lors du chargement')
      error.value = true
    } finally {
      isLoading.value = false
    }
  }

  const loadNewerThrottle = useThrottleFn(loadNewer, 5000)

  function close() {
    closeColumn(columnId.value)
    cleanColumns()
  }

  const recyclist = useTemplateRef('recyclist')
  const showScrollToTop = computed(
    () =>
      recyclist.value &&
      recyclist.value.y >= 500 &&
      !recyclist.value.directions.bottom
  )
</script>

<template>
  <section class="deck-column flex w-(--deck-column-width) flex-none flex-col">
    <SearchParams
      :column-id="columnId"
      @reset="reset"
      @close="close"
      @toggle-agenda="toggleAgenda(columnId)"
      class="z-5 px-3"
    />
    <div class="relative top-0 left-0 z-3 h-0 px-3">
      <button
        :aria-label="t('column.scroll-to-top')"
        :title="t('column.scroll-to-top')"
        class="btn btn-xs btn-block btn-soft -mt-1 -translate-y-full opacity-0 transition-transform"
        :class="{ 'translate-y-0 opacity-100': showScrollToTop }"
        @click="recyclist?.scrollToTop"
      >
        <ChevronDoubleUpIcon class="size-5" />
      </button>
    </div>
    <RecyclistNative
      ref="recyclist"
      :list="documents.map(d => d.id)"
      :size="10"
      :offset="200"
      :no-more="noMore"
      :error="error"
      :is-loading="isLoading"
      class="grow px-3"
      @load-top="loadNewerThrottle"
      @load-bottom="loadOlderThrottle"
    >
      <template #tombstone>
        <div class="flex w-full flex-col gap-4">
          <div class="skeleton h-32 w-full"></div>
          <div class="skeleton h-4 w-28"></div>
          <div class="skeleton h-4 w-full"></div>
          <div class="skeleton h-4 w-full"></div>
        </div>
      </template>
      <template #item="{ data }">
        <CardComponent
          :doc-id="data"
          :column-id="columnId"
          :search-terms="searchTerms"
        />
      </template>
      <template #nomore>
        {{ t('column.no-result') }}
      </template>
      <template #error>
        {{ t('column.error') }}
      </template>
    </RecyclistNative>
  </section>
</template>

<style scoped>
  section {
    content-visibility: auto;
  }

  @keyframes move {
    0% {
      background-position: 0 0;
    }
    100% {
      background-position: 28px 0;
    }
  }
</style>
